import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";

import ro from "./locales/ro.json";
import en from "./locales/en.json";
import hu from "./locales/hu.json";

// Supported languages. Romanian is the source of truth and the fallback:
// any key missing from `en`/`hu` renders the Romanian text rather than the raw key.
export const SUPPORTED_LANGUAGES = [
  { code: "ro", label: "Română" },
  { code: "en", label: "English" },
  { code: "hu", label: "Magyar" },
];

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      ro: { translation: ro },
      en: { translation: en },
      hu: { translation: hu },
    },
    fallbackLng: "ro",
    // Note: no explicit `lng` here on purpose — setting it would make i18next
    // skip the LanguageDetector on every load, so a stored choice would never be
    // read back (the language would reset to `ro` on each page reload).
    supportedLngs: SUPPORTED_LANGUAGES.map((l) => l.code),
    // Romanian is always the default. A ?lang= query param lets a link open the
    // site in a specific language (checked first so it overrides a stored
    // choice for that visit); otherwise we read a previously stored explicit
    // choice from localStorage. The browser language is intentionally ignored so
    // first-time visitors with no param/storage always start in Romanian, and
    // an unsupported ?lang value falls back to `ro` via supportedLngs. Full
    // URL/path-based routing is still out of scope — see
    // website-translations-plan.md if that changes.
    detection: {
      order: ["querystring", "localStorage"],
      lookupQuerystring: "lang", // ?lang=en | ?lang=hu | ?lang=ro
      lookupLocalStorage: "topspin_lang",
      // A ?lang= visit is also written to localStorage so the choice sticks on
      // subsequent pages that don't carry the param.
      caches: ["localStorage"],
    },
    interpolation: {
      escapeValue: false, // React already escapes
    },
    returnEmptyString: false, // empty string in a locale => fall back to `ro`
  });

// Strip ?lang= from the URL once the detector has consumed it. Keeps the URL
// clean and makes the header switcher authoritative after landing — otherwise a
// reload while ?lang is still present (querystring is read first) would flip the
// language back and override a manual switch.
const _url = new URL(window.location.href);
if (_url.searchParams.has("lang")) {
  _url.searchParams.delete("lang");
  window.history.replaceState({}, "", _url);
}

// Keep <html lang> in sync so assistive tech and the browser know the language.
document.documentElement.lang = i18n.resolvedLanguage || "ro";
i18n.on("languageChanged", (lng) => {
  document.documentElement.lang = lng;
});

export default i18n;
